"use client";

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from "../../../../shared/utils/index.js";
import { useEffect, useRef } from "react";
export const GlareCardRounded = ({
  children,
  className
}) => {
  const isPointerInside = useRef(false);
  const refElement = useRef(null);
  const contentRef = useRef(null);
  const state = useRef({
    glare: {
      x: 50,
      y: 50
    },
    background: {
      x: 50,
      y: 50
    },
    rotate: {
      x: 0,
      y: 0
    }
  });
  const containerStyle = {
    "--m-x": "50%",
    "--m-y": "50%",
    "--r-x": "0deg",
    "--r-y": "0deg",
    "--bg-x": "50%",
    "--bg-y": "50%",
    "--duration": "300ms",
    "--foil-size": "100%",
    "--opacity": "0",
    "--radius": "13px",
    "--easing": "ease",
    "--transition": "var(--duration) var(--easing)",
    transition: "transform var(--duration) var(--easing), opacity var(--duration) var(--easing), background var(--duration) var(--easing)"
  };
  const backgroundStyle = {
    "--step": "5%",
    "--foil-svg": `url("data:image/svg+xml,%3Csvg width='26' height='26' viewBox='0 0 26 26' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.99994 3.419C2.99994 3.419 21.6142 7.43646 22.7921 12.153C23.97 16.8695 3.41838 23.0306 3.41838 23.0306' stroke='white' stroke-width='5' stroke-miterlimit='3.86874' stroke-linecap='round' style='mix-blend-mode:darken'/%3E%3C/svg%3E")`,
    "--pattern": "var(--foil-svg) center/100% no-repeat",
    "--rainbow": "repeating-linear-gradient( 0deg,rgb(255,119,115) calc(var(--step) * 1),rgba(255,237,95,1) calc(var(--step) * 2),rgba(168,255,95,1) calc(var(--step) * 3),rgba(131,255,247,1) calc(var(--step) * 4),rgba(120,148,255,1) calc(var(--step) * 5),rgb(216,117,255) calc(var(--step) * 6),rgb(255,119,115) calc(var(--step) * 7) ) 0% var(--bg-y)/200% 700% no-repeat",
    "--diagonal": "repeating-linear-gradient( 128deg,#0e152e 0%,hsl(180,10%,60%) 3.8%,hsl(180,10%,60%) 4.5%,hsl(180,10%,60%) 5.2%,#0e152e 10%,#0e152e 12% ) var(--bg-x) var(--bg-y)/300% no-repeat",
    "--shade": "radial-gradient( farthest-corner circle at var(--m-x) var(--m-y),rgba(255,255,255,0.1) 12%,rgba(255,255,255,0.15) 20%,rgba(255,255,255,0.25) 120% ) var(--bg-x) var(--bg-y)/300% no-repeat",
    backgroundBlendMode: "hue, hue, hue, overlay"
  };
  const updateStyles = () => {
    if (refElement.current) {
      const {
        background,
        rotate,
        glare
      } = state.current;
      refElement.current?.style.setProperty("--m-x", `${glare.x}%`);
      refElement.current?.style.setProperty("--m-y", `${glare.y}%`);
      refElement.current?.style.setProperty("--r-x", `${rotate.x}deg`);
      refElement.current?.style.setProperty("--r-y", `${rotate.y}deg`);
      refElement.current?.style.setProperty("--bg-x", `${background.x}%`);
      refElement.current?.style.setProperty("--bg-y", `${background.y}%`);
    }
  };
  useEffect(() => {
    // Simulate hover effect on mount
    isPointerInside.current = true;
    if (refElement.current) {
      // Apply some initial animation
      const initialPercentage = {
        x: 75,
        y: 75
      }; // Example coordinates for initial animation
      const initialDelta = {
        x: initialPercentage.x - 50,
        y: initialPercentage.y - 50
      };
      const rotateFactor = 0.4;
      state.current.background.x = 50 + initialPercentage.x / 4 - 12.5;
      state.current.background.y = 50 + initialPercentage.y / 3 - 16.67;
      state.current.rotate.x = -(initialDelta.x / 3.5) * rotateFactor;
      state.current.rotate.y = initialDelta.y / 2 * rotateFactor;
      state.current.glare.x = initialPercentage.x;
      state.current.glare.y = initialPercentage.y;
      updateStyles();
      refElement.current.style.setProperty("--opacity", "0.1"); // Show the glare effect
      // Simulate pointer leaving after 500ms
      const timeoutId = setTimeout(() => {
        isPointerInside.current = false;
        if (refElement.current) {
          refElement.current.style.removeProperty("--duration");
          refElement.current.style.setProperty("--opacity", "0");
          refElement.current.style.setProperty("--r-x", `0deg`);
          refElement.current.style.setProperty("--r-y", `0deg`);
          // Reset background and glare to default if needed
          state.current.glare.x = 50;
          state.current.glare.y = 50;
          state.current.background.x = 50;
          state.current.background.y = 50;
          updateStyles();
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, []);
  return _jsx("div", {
    style: {
      ...containerStyle
    },
    className: "relative isolate h-auto w-full will-change-transform [contain:layout_style] [perspective:600px]",
    ref: refElement,
    onPointerMove: event => {
      const rotateFactor_0 = 0.4;
      const rect = event.currentTarget.getBoundingClientRect();
      const position = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
      const percentage = {
        x: 100 / rect.width * position.x,
        y: 100 / rect.height * position.y
      };
      const delta = {
        x: percentage.x - 50,
        y: percentage.y - 50
      };
      const {
        background: background_0,
        rotate: rotate_0,
        glare: glare_0
      } = state.current;
      background_0.x = 50 + percentage.x / 4 - 12.5;
      background_0.y = 50 + percentage.y / 3 - 16.67;
      rotate_0.x = -(delta.x / 3.5);
      rotate_0.y = delta.y / 2;
      rotate_0.x *= rotateFactor_0;
      rotate_0.y *= rotateFactor_0;
      glare_0.x = percentage.x;
      glare_0.y = percentage.y;
      updateStyles();
    },
    onPointerEnter: () => {
      isPointerInside.current = true;
      if (refElement.current) {
        setTimeout(() => {
          if (isPointerInside.current) {
            refElement.current?.style.setProperty("--duration", "0s");
          }
        }, 300);
      }
    },
    onPointerLeave: () => {
      isPointerInside.current = false;
      if (refElement.current) {
        refElement.current.style.removeProperty("--duration");
        refElement.current?.style.setProperty("--r-x", `0deg`);
        refElement.current?.style.setProperty("--r-y", `0deg`);
      }
    },
    children: _jsxs("div", {
      className: "grid h-full w-full origin-center overflow-hidden rounded-full border-none will-change-transform [transform:rotateY(var(--r-x))_rotateX(var(--r-y))] hover:filter-none hover:[--duration:200ms] hover:[--easing:linear] hover:[--opacity:0.1]",
      children: [_jsx("div", {
        className: "grid h-full w-full mix-blend-soft-light [clip-path:inset(0_0_0_0_round_var(--radius))] [grid-area:1/1]",
        children: _jsx("div", {
          ref: contentRef,
          className: cn("h-full w-full object-cover", className),
          children: children
        })
      }), _jsx("div", {
        className: "grid h-full w-full opacity-[var(--opacity)] mix-blend-soft-light [grid-area:1/1] [background:radial-gradient(farthest-corner_circle_at_var(--m-x)_var(--m-y),_rgba(255,255,255,0.8)_10%,_rgba(255,255,255,0.65)_20%,_rgba(255,255,255,0)_90%)] [clip-path:inset(0_0_1px_0_round_var(--radius))]",
        style: {
          transition: "opacity var(--duration) var(--easing), background var(--duration) var(--easing)"
        }
      }), _jsx("div", {
        className: "relative grid h-full w-full opacity-[var(--opacity)] mix-blend-color-dodge transition-opacity [grid-area:1/1] [clip-path:inset(0_0_1px_0_round_var(--radius))] [background-blend-mode:hue_hue_hue_overlay] [background:var(--pattern),_var(--rainbow),_var(--diagonal),_var(--shade)] after:bg-[inherit] after:mix-blend-exclusion after:content-[''] after:[background-blend-mode:soft-light,_hue,_hard-light] after:[background-position:center,_0%_var(--bg-y),_calc(var(--bg-x)*_-1)_calc(var(--bg-y)*_-1),_var(--bg-x)_var(--bg-y)] after:[background-size:var(--foil-size),_200%_400%,_800%,_200%]",
        style: {
          ...backgroundStyle
        }
      })]
    })
  });
};